import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FeedbackSection() {
  const [form, setForm] = useState({ full_name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    // Read directly from DOM — defensive against browser autofill not firing onChange
    const formEl = e.currentTarget;
    const domName = (formEl.querySelector('[data-testid="feedback-full-name"]')?.value || form.full_name).trim();
    const domEmail = (formEl.querySelector('[data-testid="feedback-email"]')?.value || form.email).trim();
    const domSubject = (formEl.querySelector('[data-testid="feedback-subject"]')?.value || form.subject).trim();
    const domMessage = (formEl.querySelector('[data-testid="feedback-message"]')?.value || form.message).trim();

    if (domName.length < 2) {
      toast.error("Please enter your name.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(domEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (domMessage.length < 5) {
      toast.error("Please write a short message.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        full_name: domName,
        email: domEmail,
        subject: domSubject || undefined,
        message: domMessage,
      });
      setSent(true);
      toast.success("Thanks — your message has been received.");
      setForm({ full_name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send your message. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't send your message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="feedback" className="bg-[#F9F8F6]" data-testid="feedback-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <p className="overline mb-4">Ask a question</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl tracking-tight">
              Can’t make an information day? Send us a note.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[#2A2B2A]/85">
              Use this form for general questions, observations, or anything else you
              would like to raise with the project team. Your message goes
              directly to the Community Liaison Officer.
            </p>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-7 bg-white border border-[#E0DCD3] rounded-sm p-7 lg:p-10 reveal" data-testid="feedback-form">
            {sent ? (
              <div className="text-center py-10" data-testid="feedback-success">
                <p className="overline mb-3">Message received</p>
                <h3 className="font-serif-display text-2xl tracking-tight">Thank you for getting in touch.</h3>
                <p className="mt-3 text-[14.5px] text-[#63635E]">We aim to respond within five working days.</p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="feedback-send-another"
                  className="mt-6 inline-flex items-center h-10 px-4 rounded-sm border border-[#4C4538] text-[#4C4538] text-sm font-medium hover:bg-[#4C4538] hover:text-white transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field label="Your name *">
                    <input
                      required
                      minLength={2}
                      type="text"
                      value={form.full_name}
                      onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                      data-testid="feedback-full-name"
                      className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="feedback-email"
                      className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
                    />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Subject (optional)">
                    <input
                      type="text"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      data-testid="feedback-subject"
                      className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30"
                    />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Your message *">
                    <textarea
                      required
                      minLength={5}
                      rows={6}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="feedback-message"
                      className="w-full bg-[#F9F8F6] border border-[#E0DCD3] rounded-sm px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[#3A4B3A] focus:ring-2 focus:ring-[#8C9B86]/30 resize-none"
                    />
                  </Field>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="feedback-submit"
                    className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-sm bg-[#3A4B3A] text-white text-sm font-medium hover:bg-[#2A3B2A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {submitting ? "Sending…" : "Send message"}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-[#4C4538] mb-1.5">{label}</span>
      {children}
    </label>
  );
}
